import React from "react";
import { Link } from "react-router-dom";
import { LegalHeader } from "./Terms";

/**
 * Public privacy policy page tailored to the NMI Provider Network Manager.
 * Aligned with Ghana's Data Protection Act, 2012 (Act 843): identifies the
 * data controller, categories of data processed, lawful bases, retention,
 * data-subject rights, cross-border transfers, and the Data Protection
 * Commission complaint route.
 */
export default function Privacy() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="privacy-page">
      <LegalHeader />

      <main className="mx-auto max-w-3xl px-6 py-10 pb-20">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-nmi-700">
          Legal · Effective Feb 18, 2026
        </div>
        <h1 className="mt-2 font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
          Privacy Policy
        </h1>
        <p className="mt-3 text-sm text-slate-600 leading-relaxed">
          Nationwide Medical Insurance Ghana Ltd. ("NMI", "we", "us") is
          the data controller for personal data processed by the
          <b> Provider Network Manager</b> and its Telegram bot companion.
          This policy explains what we collect, why, how we protect it,
          and the rights available to you under the
          <b> Data Protection Act, 2012 (Act 843)</b> of Ghana.
        </p>

        <Section n="1" title="Who this applies to">
          This policy covers NMI staff, approved auditors, and Telegram
          users who have been provisioned access to the Service. It also
          covers third-party <b>healthcare service provider (HSP) contacts</b>
          — the facility administrators, front-desk numbers, and Google
          Maps references stored in the network directory — where those
          contact details identify a natural person.
        </Section>

        <Section n="2" title="Personal data we process">
          <p><b>About account holders (you):</b></p>
          <ul className={ul}>
            <li>Name, work email, and role (used to sign you in and gate features).</li>
            <li>Password hash (bcrypt — we never see the plaintext).</li>
            <li>Telegram user ID and public username, if you bind a Telegram account.</li>
            <li>Sign-in timestamps, IP addresses (via <code>X-Forwarded-For</code>) for rate-limiting and brute-force protection, and browser session cookies.</li>
            <li>Full audit trail of every write action (create, edit, suspend, reactivate, terminate, import, role change, merge).</li>
          </ul>
          <p className="mt-3"><b>About HSP contacts (third parties):</b></p>
          <ul className={ul}>
            <li>Facility name, category, region, town, district, GPS/GhanaPost, Google Maps link.</li>
            <li>Reception and administrator phone numbers, primary contact person.</li>
            <li>Company/plan access assignments, historical status changes with reasons.</li>
          </ul>
        </Section>

        <Section n="3" title="Why we process this data (lawful bases)">
          <ul className={ul}>
            <li><b>Contract & legitimate interest:</b> to run NMI's provider network operations — approve facilities, suspend under-performing ones, notify staff, and generate reports.</li>
            <li><b>Legal obligation:</b> to keep auditable records of every write action for regulatory review.</li>
            <li><b>Consent:</b> for optional features you opt into — Telegram broadcast subscriptions, watchlist digest emails.</li>
            <li><b>Security:</b> to detect and block credential-stuffing, throttle brute-force attempts, and monitor for anomalies.</li>
          </ul>
        </Section>

        <Section n="4" title="How we use it">
          <ul className={ul}>
            <li>Authenticate you and route you to features your role permits.</li>
            <li>Send transactional emails (password reset, watchlist digest) via <b>Resend</b>. No marketing.</li>
            <li>Deliver Telegram bot responses via the official Telegram Bot API.</li>
            <li>Detect and pre-empt security incidents (via <b>Sentry</b> for error monitoring, and internal rate-limits).</li>
            <li>Improve the Service — usage patterns are reviewed in aggregate, never sold.</li>
          </ul>
        </Section>

        <Section n="5" title="AI features">
          The Service integrates optional AI assistance (bulk-edit
          suggestions, district inference). Facility fields you submit
          for AI processing are sent to our LLM provider under a data-
          processing agreement. We do not send account passwords, session
          tokens, or member health data to the AI provider. AI is used
          only to assist an authenticated NMI operator; no decision is
          automated without human review.
        </Section>

        <Section n="6" title="Sharing and disclosure">
          We share personal data only:
          <ul className={ul}>
            <li>With the sub-processors listed in <b>section 8</b>, under written data-processing terms.</li>
            <li>With regulators, auditors, or law enforcement when we have a legal obligation or a valid subpoena.</li>
            <li>Within NMI, on a strict need-to-know basis governed by roles.</li>
          </ul>
          We never sell your personal data.
        </Section>

        <Section n="7" title="Retention">
          <ul className={ul}>
            <li><b>Account records:</b> for the duration of your role and 24 months thereafter, unless a longer legal-retention obligation applies.</li>
            <li><b>Audit logs:</b> retained indefinitely for compliance and forensic review — this cannot be deleted on request.</li>
            <li><b>Sign-in attempts and throttle counters:</b> rolling 30-day window.</li>
            <li><b>Password reset tokens:</b> 60 minutes then automatically purged.</li>
            <li><b>HSP contact records:</b> retained while the facility remains in-network; historical status records are archived.</li>
          </ul>
        </Section>

        <Section n="8" title="Sub-processors">
          <div className="mt-2 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-100">
                  <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-slate-500">Provider</th>
                  <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-slate-500">Purpose</th>
                  <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-slate-500">Region</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {[
                  ["MongoDB Atlas", "Primary database", "AWS · region as configured"],
                  ["Emergent Platform", "Hosting & deployment", "AWS / GCP · region as configured"],
                  ["Telegram Bot API", "Bot messaging", "Global"],
                  ["Resend", "Transactional email delivery", "EU / US"],
                  ["Sentry", "Error monitoring", "EU / US"],
                  ["Emergent LLM (Anthropic Claude)", "Optional AI assist", "US"],
                ].map(([n, p, r]) => (
                  <tr key={n} className="bg-white">
                    <td className="px-3 py-2 font-semibold text-slate-800">{n}</td>
                    <td className="px-3 py-2 text-slate-700">{p}</td>
                    <td className="px-3 py-2 text-slate-500">{r}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section n="9" title="Cross-border transfers">
          Some sub-processors above operate servers outside Ghana. We rely
          on standard contractual clauses and each provider's own
          adequacy certifications to protect data in transit. By using the
          Service you consent to these transfers, which are essential to
          delivering the platform.
        </Section>

        <Section n="10" title="Security">
          <ul className={ul}>
            <li>Passwords hashed with bcrypt (12 rounds).</li>
            <li>Sessions carried in short-lived (1h) JWT + 7-day refresh tokens, both stored as <code>httpOnly</code>, <code>Secure</code>, <code>SameSite=Lax</code> cookies.</li>
            <li>Rate-limiting on sign-in, password reset, import, and AI endpoints.</li>
            <li>Role- and permission-scoped API surface. Every write is audit-logged.</li>
            <li>HTTPS/TLS enforced on all traffic; production traffic terminates at an ingress under NMI control.</li>
          </ul>
        </Section>

        <Section n="11" title="Your rights under the DPA">
          You have the right to:
          <ul className={ul}>
            <li><b>Access</b> the personal data we hold about you.</li>
            <li><b>Correct</b> inaccurate data (typically via a request to your admin, who edits your row on the Users page).</li>
            <li><b>Object</b> to processing you consider unlawful.</li>
            <li><b>Withdraw consent</b> for optional features (e.g. Telegram broadcasts, watchlist digest).</li>
            <li><b>Erasure</b> where a legal-retention obligation does not require us to keep the record.</li>
          </ul>
          To exercise any of these rights, contact us using the details in <b>section 13</b>. We aim to respond within 21 days.
        </Section>

        <Section n="12" title="Complaints">
          If you believe we have not handled your personal data properly,
          please contact us first so we can put it right. You may also
          lodge a complaint directly with the Ghana Data Protection
          Commission:
          <div className="mt-2 rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm">
            <div><b>Data Protection Commission — Ghana</b></div>
            <div>C550/3, Kanda High Street, Accra</div>
            <div>Website: <a className={link} href="https://www.dataprotection.org.gh" target="_blank" rel="noreferrer">dataprotection.org.gh</a></div>
          </div>
        </Section>

        <Section n="13" title="Contact us">
          <div className="mt-2 rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm">
            <div><b>Nationwide Medical Insurance Ghana Ltd.</b></div>
            <div>Attn: Data Protection Lead — Provider Network Team</div>
            <div>Email: <a className={link} href="mailto:privacy@nationwidemh.com">privacy@nationwidemh.com</a></div>
            <div>General: <a className={link} href="mailto:pnm@nationwidemh.com">pnm@nationwidemh.com</a></div>
          </div>
        </Section>

        <Section n="14" title="Updates">
          We review this policy at least annually. Material updates will
          be announced via in-app banner and email. The date at the top
          of this page reflects the most recent revision.
        </Section>

        <div className="mt-10 rounded-md border border-slate-200 bg-white p-4 text-xs text-slate-500">
          Also see our <Link to="/terms" className={link}>Terms of Service</Link>.
        </div>
      </main>
    </div>
  );
}

const link = "font-semibold text-nmi-700 underline underline-offset-2 hover:text-nmi-800";
const ul = "mt-2 list-disc pl-5 space-y-1.5 text-sm text-slate-700 leading-relaxed";

const Section = ({ n, title, children }) => (
  <section className="mt-8 border-t border-slate-200 pt-6">
    <h2 className="font-heading text-lg font-bold text-slate-900">
      <span className="mr-2 text-slate-400">{n}.</span>{title}
    </h2>
    <div className="mt-2 text-sm text-slate-700 leading-relaxed">{children}</div>
  </section>
);
